import * as yup from 'yup';
import { DocumentNode } from 'graphql';
import { addDays, differenceInDays } from 'date-fns';
import { useForm } from 'react-form-state-manager';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GetOrderQuery as GetOrder } from '__generated__/graphql';

import { PaymentMethod } from '../../../../common/usePaymentMethods';
import { filterPrefix, getServerErrors } from '../../../../common/helpers';
import { hasInvoiceDetails } from '../../../../common/Invoice/InvoiceDetails';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import ContactPicker, { Participant } from '../../Contacts/ContactPicker';
import EditOrderFromBackendMutation from './EditOrderFromBackendMutation';
import GetOrderQuery from './GetOrderQuery';
import InvoiceDetailsForm, { getInvoiceSchema } from '../../../../common/Invoice/InvoiceDetailsForm';
import PaymentReminderInfo from '../../../Common/PaymentReminderInfo';
import UI from '../../../../common/UI';
import useIntegerInput from '../../../../common/useIntegerInput';
import useLocale from '../../../../common/useLocale';
import useMutation from '../../../../api/useMutation';
import useProject from '../../../useProject';
import useScroll from '../../../../common/useScroll';
import useValidation from '../../../../common/useValidation';

type Order = GetOrder['order'];

export interface EditInvoiceFormProps {
  order: Order;
  onSuccess?: () => void;
  onCancel?: () => void;
  refetchQueries?: DocumentNode[];
}

const EditInvoiceForm = ({
  order, onSuccess, onCancel, refetchQueries = [GetOrderQuery],
}: EditInvoiceFormProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const notifier = useNotifier();
  const { scrollToIfInvisible } = useScroll();
  const { formatDate, parseDate } = useLocale();
  const integerInput = useIntegerInput();

  const invoice = {
    company_name: order.company_name,
    is_charity: order.is_charity,
    reference: order.reference,
    vat_id: order.vat_id,
    street: order.street,
    house_number: order.house_number,
    zip_code: order.zip_code,
    city: order.city,
    extra_address_line: order.extra_address_line,
    country: order.country,
    phone: order.phone,
  };

  const getValues = (order: Order) => {
    const invoiceDate = parseDate(order.invoice_date, { format: 'internal_date' });
    const dueDate = parseDate(order.due_date, { format: 'internal_date' });
    const paymentTerm = differenceInDays(dueDate, invoiceDate);

    return {
      participant: {
        id: order.participant.id,
      },
      invoice: {
        ...invoice,
        country: order.country || project.organisation_country,
      },
      unset_invoice_details: !hasInvoiceDetails(invoice),
      invoice_date: order.invoice_date,
      payment_term: paymentTerm,
      start_bank_transfer_payment: order.start_bank_transfer_payment,
      cancel_after_due_date: order.cancel_after_due_date,
    };
  };

  const form = useForm({
    values: getValues(order),
  });

  const invoiceDetails = !form.values.unset_invoice_details ? {
    ...form.values.invoice,
    // Do not send VAT ID if it was not changed, so that it is not validated by the server.
    vat_id: order.vat_id_editable ? form.values.invoice.vat_id : null,
  } : null;

  const invoiceDate = form.values.invoice_date
    ? parseDate(form.values.invoice_date, { format: 'internal_date' })
    : new Date();
  const dueDate = addDays(invoiceDate, form.values.payment_term);

  const [editOrder, { error, loading }] = useMutation(
    EditOrderFromBackendMutation,
    {
      variables: {
        input: {
          id: order.id,
          participant: {
            id: form.values.participant.id,
          },
          invoice: !form.values.unset_invoice_details ? {
            ...form.values.invoice,
            // Do not send VAT ID if it was not changed, so that it is not validated by the server.
            vat_id: order.vat_id_editable ? form.values.invoice.vat_id : null,
          } : null,
          unset_invoice_details: form.values.unset_invoice_details,
          invoice_date: form.values.invoice_date,
          due_date: formatDate(dueDate, { format: 'internal_date', timezone: null }),
          start_bank_transfer_payment: form.values.start_bank_transfer_payment,
          cancel_after_due_date: form.values.cancel_after_due_date,
        },
      },
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        notifier.success(t('notifications.order_edited'));
        onSuccess?.();
      },
      onError: () => {
        scrollToIfInvisible('EditInvoiceForm');
      },
    },
  );

  const [participant, setParticipant] = useState<Participant>({
    id: order.participant.id,
    full_name: `${order.participant.first_name} ${order.participant.last_name}`,
    email: order.participant.email,
  });

  const handleChangeParticipant = (participant: Participant) => {
    form.set('participant.id', participant.id);
    setParticipant(participant);
  };

  const hasPendingPayments = order.payments.data.some((payment) => payment.status === 'pending');
  const bankTransferEnabled = order.event.payment_methods.includes(PaymentMethod.BankTransfer);

  const { errors, valid } = useValidation({
    schema: yup.object({
      invoice: getInvoiceSchema().nullable(),
      invoice_date: yup.string().ensure().required(),
    }),
    values: {
      invoice: invoiceDetails,
      invoice_date: form.values.invoice_date,
    },
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editOrder()} id="EditInvoiceForm">
      <UI.FormGrid>
        <UI.Legend>
          {t('edit_invoice_details')}
        </UI.Legend>

        <UI.ServerErrors error={error} />

        <UI.InputGroup>
          <UI.InputLabel sc={{ mb: 0 }}>
            {t('contact')}
          </UI.InputLabel>
          <ContactPicker
            participant={participant}
            onChange={(participant) => handleChangeParticipant(participant)}
            nullable={false}
          />
        </UI.InputGroup>

        <UI.InputGroup>
          <UI.Checkbox
            checked={!form.values.unset_invoice_details}
            onChange={() => form.set('unset_invoice_details', (value: boolean) => !value)}
            name="specify_details"
          >
            {t('specify_invoice_details')}
          </UI.Checkbox>
        </UI.InputGroup>

        {!form.values.unset_invoice_details && (
          <InvoiceDetailsForm
            value={form.values.invoice}
            onChange={(value) => form.set((input) => ({ ...input, invoice: { ...input.invoice, ...value } }))}
            onBlur={(field) => form.setTouched(`invoice.${field}`, true)}
            touched={form.touched.invoice}
            errors={filterPrefix(errors, 'invoice')}
            disableVatId={!order.vat_id_editable}
          />
        )}

        <UI.InputGroup>
          <UI.InputLabel htmlFor="OrderInvoiceDate">
            {t('invoice_date')}
          </UI.InputLabel>
          <UI.InputDescription>
            {t('invoice_date_description')}
          </UI.InputDescription>
          <UI.DatePicker
            id="OrderInvoiceDate"
            selected={form.values.invoice_date}
            onChange={(value) => form.set('invoice_date', value)}
            minDate={formatDate(new Date(), { timezone: null, format: 'internal_date' })}
            disabled={!order.draft}
          />
          {order.remaining_amount > 0 && !hasPendingPayments && bankTransferEnabled && (
            <UI.Checkbox
              {...form.checkbox('start_bank_transfer_payment')}
              id="OrderStartBankTransferPayment"
              sc={{ mt: 2 }}
            >
              {t('add_bank_transfer_reference')}
            </UI.Checkbox>
          )}
          <UI.ErrorMessages
            attribute={t('invoice_date')}
            errors={errors.invoice_date}
          />
        </UI.InputGroup>

        <UI.InputGroup>
          <UI.InputLabel htmlFor="OrderPaymentTerm">
            {t('payment_term_in_days')}
          </UI.InputLabel>
          <UI.InputWrapper>
            <UI.DebouncedInput
              {...form.text('payment_term', { inputHandler: integerInput() })}
              maxLength={3}
              format={integerInput().format}
              inputMode="numeric"
              match={/[\d]/g}
              id="OrderPaymentTerm"
              placeholder="0"
            />
            <UI.Span sc={{ muted: true }}>
              {formatDate(dueDate, { format: 'display_date', timezone: null })}
            </UI.Span>
          </UI.InputWrapper>
          {order.remaining_amount > 0 && (
            <UI.Checkbox
              {...form.checkbox('cancel_after_due_date')}
              id="OrderCancelAfterDueDate"
              sc={{ mt: 2 }}
            >
              {t('cancel_purchases_after_due_date')}
            </UI.Checkbox>
          )}
          <UI.ErrorMessages
            attribute={t('payment_term_in_days')}
            errors={errors.due_date}
          />
        </UI.InputGroup>

        <PaymentReminderInfo dueDate={dueDate} />

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save')}
          loading={loading}
          valid={valid}
          onClose={onCancel}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditInvoiceForm;
